import { useState, useEffect } from 'react';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Plus, Ticket, Search, Filter, Clock, User, MapPin, MessageSquare,
  AlertTriangle, AlertCircle, CheckCircle, XCircle, Pause, Send,
  Wrench, Zap, Wifi, Phone, Sparkles, Shield, Headphones, MoreHorizontal
} from 'lucide-react';
import { toast } from 'sonner';

const DEPARTMENT_OPTIONS = [
  { value: 'plumbing', label: 'Plumbing', icon: Wrench, color: 'bg-blue-100 text-blue-700' },
  { value: 'electrical', label: 'Electrical', icon: Zap, color: 'bg-yellow-100 text-yellow-700' },
  { value: 'networking_it', label: 'Networking/IT', icon: Wifi, color: 'bg-purple-100 text-purple-700' },
  { value: 'telephone', label: 'Telephone', icon: Phone, color: 'bg-green-100 text-green-700' },
  { value: 'housekeeping', label: 'Housekeeping', icon: Sparkles, color: 'bg-pink-100 text-pink-700' },
  { value: 'hvac', label: 'HVAC', icon: Wrench, color: 'bg-cyan-100 text-cyan-700' },
  { value: 'maintenance', label: 'Maintenance', icon: Wrench, color: 'bg-orange-100 text-orange-700' },
  { value: 'security', label: 'Security', icon: Shield, color: 'bg-red-100 text-red-700' },
  { value: 'front_desk', label: 'Front Desk', icon: Headphones, color: 'bg-indigo-100 text-indigo-700' },
  { value: 'other', label: 'Other', icon: MoreHorizontal, color: 'bg-gray-100 text-gray-700' }
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-700' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-700' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-700' }
];

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open', icon: AlertCircle, color: 'bg-blue-100 text-blue-700' },
  { value: 'in_progress', label: 'In Progress', icon: Clock, color: 'bg-yellow-100 text-yellow-700' },
  { value: 'pending', label: 'Pending', icon: Pause, color: 'bg-purple-100 text-purple-700' },
  { value: 'resolved', label: 'Resolved', icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700' },
  { value: 'closed', label: 'Closed', icon: XCircle, color: 'bg-gray-100 text-gray-700' }
];

import { useUser } from '@/utils/userHelper';

export default function SupportTickets({ user: propUser, isTab, tabId }) {
  const user = useUser(propUser);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [newComment, setNewComment] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    department: 'maintenance',
    priority: 'medium',
    location_type: 'room',
    location_name: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [ticketsRes, statsRes, usersRes] = await Promise.all([
        api.get('/tickets'),
        api.get('/tickets/stats/summary'),
        api.get('/users')
      ]);
      setTickets(ticketsRes.data);
      setStats(statsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      department: 'maintenance',
      priority: 'medium',
      location_type: 'room',
      location_name: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/tickets', formData);
      toast.success('Ticket created');
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Failed to create ticket');
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await api.put(`/tickets/${ticketId}`, { status: newStatus });
      toast.success('Status updated');
      loadData();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status: newStatus });
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAssign = async (ticketId, userId) => {
    try {
      await api.patch(`/tickets/${ticketId}/assign?user_id=${userId}`);
      toast.success('Ticket assigned');
      loadData();
    } catch (error) {
      toast.error('Failed to assign ticket');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTicket) return;
    try {
      await api.post(`/tickets/${selectedTicket.id}/comments`, { comment: newComment });
      toast.success('Comment added');
      setNewComment('');
      // Refresh ticket details
      const res = await api.get(`/tickets/${selectedTicket.id}`);
      setSelectedTicket(res.data);
      loadData();
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const openTicketDetail = async (ticket) => {
    try {
      const res = await api.get(`/tickets/${ticket.id}`);
      setSelectedTicket(res.data);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.ticket_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDept = filterDepartment === 'all' || ticket.department === filterDepartment;
    const matchesStatus = filterStatus === 'all' || ticket.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || ticket.priority === filterPriority;
    return matchesSearch && matchesDept && matchesStatus && matchesPriority;
  });

  const getDeptInfo = (dept) => DEPARTMENT_OPTIONS.find(d => d.value === dept) || DEPARTMENT_OPTIONS[9];
  const getPriorityInfo = (priority) => PRIORITY_OPTIONS.find(p => p.value === priority) || PRIORITY_OPTIONS[1];
  const getStatusInfo = (status) => STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6" data-testid="support-tickets">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Manrope' }}>
            Support Tickets
          </h1>
          <p className="text-gray-500 text-sm mt-1">Manage internal support requests</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-orange hover:opacity-90 text-white" data-testid="create-ticket-btn">
              <Plus className="h-4 w-4 mr-2" />
              Create Ticket
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle className="text-gray-800">Create Support Ticket</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  placeholder="Brief description of the issue"
                  className="border-orange-200"
                  data-testid="ticket-title-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Department *</Label>
                  <Select value={formData.department} onValueChange={(v) => setFormData({...formData, department: v})}>
                    <SelectTrigger className="border-orange-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DEPARTMENT_OPTIONS.map(dept => (
                        <SelectItem key={dept.value} value={dept.value}>
                          <span className="flex items-center gap-2">
                            <dept.icon className="h-4 w-4" />
                            {dept.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Priority *</Label>
                  <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                    <SelectTrigger className="border-orange-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITY_OPTIONS.map(p => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Location Type</Label>
                  <Select value={formData.location_type} onValueChange={(v) => setFormData({...formData, location_type: v})}>
                    <SelectTrigger className="border-orange-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="room">Room</SelectItem>
                      <SelectItem value="lobby">Lobby</SelectItem>
                      <SelectItem value="restaurant">Restaurant</SelectItem>
                      <SelectItem value="pool">Pool Area</SelectItem>
                      <SelectItem value="parking">Parking</SelectItem>
                      <SelectItem value="kitchen">Kitchen</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Location Name</Label>
                  <Input
                    value={formData.location_name}
                    onChange={(e) => setFormData({...formData, location_name: e.target.value})}
                    placeholder="e.g., Room 101"
                    className="border-orange-200"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                  placeholder="Detailed description of the issue..."
                  className="border-orange-200"
                  rows={4}
                  data-testid="ticket-description-input"
                />
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-gradient-orange text-white" data-testid="submit-ticket-btn">
                  Create Ticket
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-orange-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Ticket className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
                  <p className="text-xs text-gray-500">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-orange-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.open}</p>
                  <p className="text-xs text-gray-500">Open</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-orange-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.in_progress}</p>
                  <p className="text-xs text-gray-500">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-orange-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.resolved}</p>
                  <p className="text-xs text-gray-500">Resolved</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-orange-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.sla_breached}</p>
                  <p className="text-xs text-gray-500">SLA Breached</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="border-orange-100">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search tickets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-orange-200"
                />
              </div>
            </div>
            <Select value={filterDepartment} onValueChange={setFilterDepartment}>
              <SelectTrigger className="w-[160px] border-orange-200">
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {DEPARTMENT_OPTIONS.map(d => (
                  <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[140px] border-orange-200">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {STATUS_OPTIONS.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-[130px] border-orange-200">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                {PRIORITY_OPTIONS.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Ticket List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="h-10 w-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTickets.map(ticket => {
            const deptInfo = getDeptInfo(ticket.department);
            const priorityInfo = getPriorityInfo(ticket.priority);
            const statusInfo = getStatusInfo(ticket.status);
            const DeptIcon = deptInfo.icon;
            const StatusIcon = statusInfo.icon;
            
            return (
              <Card 
                key={ticket.id} 
                className="border-orange-100 hover:border-orange-300 hover:shadow-md transition-all cursor-pointer"
                onClick={() => openTicketDetail(ticket)}
                data-testid={`ticket-card-${ticket.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${deptInfo.color}`}>
                      <DeptIcon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-semibold text-gray-800">{ticket.title}</p>
                          <p className="text-sm text-gray-500">{ticket.ticket_number}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={priorityInfo.color}>{priorityInfo.label}</Badge>
                          <Badge className={statusInfo.color}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusInfo.label}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        {ticket.location_name && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {ticket.location_name}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {ticket.created_by_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(ticket.created_at)}
                        </span>
                        {ticket.sla_breached && (
                          <span className="flex items-center gap-1 text-red-600">
                            <AlertTriangle className="h-3 w-3" />
                            SLA Breached
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {!loading && filteredTickets.length === 0 && (
        <Card className="border-orange-100">
          <CardContent className="py-12 text-center">
            <Ticket className="h-12 w-12 mx-auto text-orange-300 mb-4" />
            <p className="text-gray-500">No tickets found</p>
            <p className="text-sm text-gray-400 mt-2">Create your first support ticket</p>
          </CardContent>
        </Card>
      )}

      {/* Ticket Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-gray-800">
              Ticket Details - {selectedTicket?.ticket_number}
            </DialogTitle>
          </DialogHeader>
          {selectedTicket && (
            <div className="flex-1 overflow-y-auto space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">{selectedTicket.title}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={getDeptInfo(selectedTicket.department).color}>
                      {getDeptInfo(selectedTicket.department).label}
                    </Badge>
                    <Badge className={getPriorityInfo(selectedTicket.priority).color}>
                      {getPriorityInfo(selectedTicket.priority).label}
                    </Badge>
                  </div>
                </div>
                <Select 
                  value={selectedTicket.status} 
                  onValueChange={(v) => handleStatusChange(selectedTicket.id, v)}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-700 whitespace-pre-wrap">{selectedTicket.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Location</p>
                  <p className="font-medium text-gray-800">
                    {selectedTicket.location_name || selectedTicket.location_type}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Created By</p>
                  <p className="font-medium text-gray-800">{selectedTicket.created_by_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Created At</p>
                  <p className="font-medium text-gray-800">{formatDate(selectedTicket.created_at)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Due Date</p>
                  <p className={`font-medium ${selectedTicket.sla_breached ? 'text-red-600' : 'text-gray-800'}`}>
                    {formatDate(selectedTicket.due_date)}
                    {selectedTicket.sla_breached && ' (Breached)'}
                  </p>
                </div>
              </div>

              {/* Assignment */}
              <div className="flex items-center gap-4">
                <Label className="text-gray-700">Assigned To:</Label>
                <Select 
                  value={selectedTicket.assigned_to || 'unassigned'}
                  onValueChange={(v) => v !== 'unassigned' && handleAssign(selectedTicket.id, v)}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Select staff" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Comments */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Comments ({selectedTicket.comments?.length || 0})
                </h4>
                <ScrollArea className="h-[150px] border rounded-lg p-3">
                  {selectedTicket.comments?.length > 0 ? (
                    <div className="space-y-3">
                      {selectedTicket.comments.map(comment => (
                        <div key={comment.id} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm text-gray-800">{comment.user_name}</span>
                            <span className="text-xs text-gray-500">{formatDate(comment.created_at)}</span>
                          </div>
                          <p className="text-sm text-gray-600">{comment.comment}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">No comments yet</p>
                  )}
                </ScrollArea>
                <div className="flex gap-2 mt-3">
                  <Input
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="flex-1 border-orange-200"
                    onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                  />
                  <Button onClick={handleAddComment} className="bg-gradient-orange text-white">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
